import React from 'react';
import Switch, { SwitchProps } from '@mui/material/Switch';
import { alpha, styled } from '@mui/material/styles';
import clsx from 'clsx';
import { colors } from '../theme/tokens.colors';
import { radius } from '../theme/tokens.radius';
import UIKitIcon from './Icon';
import { ThemeColor } from '../theme';
import { uikitPalette } from '../theme/uikitPalette';

export type UIKitToggleSize = 'medium' | 'small';

export interface UIKitToggleProps extends Omit<SwitchProps, 'color' | 'size'> {
  size?: UIKitToggleSize;
  /** si true, muestra iconos (check/remove) dentro del handle */
  hasIcon?: boolean;
  /** Color de tema para el estado ON (primary, secondary, success, etc.) */
  color?: ThemeColor;
}

// JSS permitía referenciar otra regla del mismo makeStyles() con "$otroKey"
// (ej. "&:hover $thumbInnerChecked") para que el hover del root afecte al
// thumb interno, un nodo hijo custom que NO es parte del Switch de MUI.
// Con styled() no hay una referencia cruzada equivalente entre componentes
// styled distintos - se usan estos 2 nombres de clase FIJOS (no generados
// por emotion) como hooks estables, igual que hacía JSS con sus nombres de
// clase predecibles (.makeStyles-thumbInnerChecked-123), y se seleccionan
// por descendencia desde el propio Switch.
const THUMB_CHECKED_CLASS = 'uikit-toggle-thumb--checked';
const THUMB_UNCHECKED_CLASS = 'uikit-toggle-thumb--unchecked';

const StyledSwitch = styled(Switch, {
  shouldForwardProp: (prop) => !['uiSize', 'paletteMain', 'paletteLight'].includes(prop as string),
})<{ uiSize: UIKitToggleSize; paletteMain: string; paletteLight: string }>(
  ({ theme, uiSize, paletteMain, paletteLight }) => {
    const small = uiSize === 'small';
    const halo = small ? 3 : 4;

    return {
      /* ---------- COMÚN (root) ---------- */
      width: small ? 38 : 52,
      height: small ? 22 : 32,
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',

      // halo sólo cuando está ON
      [`&:not(.Mui-disabled):hover .${THUMB_CHECKED_CLASS}, &:not(.Mui-disabled):active .${THUMB_CHECKED_CLASS}`]:
        {
          boxShadow: `0 0 0 ${halo}px ${alpha(paletteMain, 0.24)}`,
        },

      /* ---------- SWITCH BASE ---------- */
      '& .MuiSwitch-switchBase': {
        padding: small ? 2 : 4,
        top: small ? 2 : 3,
        left: small ? 2 : undefined,
        transition: theme.transitions.create(['transform']),
        '& + .MuiSwitch-track': {
          backgroundColor: paletteLight,
          borderColor: paletteLight,
          opacity: 1,
        },

        '&.Mui-checked': {
          top: 0,
          left: small ? 0 : undefined,
          transform: small ? 'translateX(16px)' : 'translateX(20px)',
          '& + .MuiSwitch-track': {
            backgroundColor: paletteMain,
            borderColor: paletteMain,
            opacity: 1,
          },
        },

        [`&.MuiIconButton-root:hover .${THUMB_CHECKED_CLASS}, &.MuiIconButton-root:active .${THUMB_CHECKED_CLASS}`]:
          {
            boxShadow: `0 0 0 ${halo}px ${alpha(paletteMain, 0.24)}`,
          },

        [`&.Mui-focusVisible .${THUMB_CHECKED_CLASS}`]: {
          boxShadow: `0 0 0 ${halo}px ${alpha(paletteMain, 0.24)}`,
        },

        '&.Mui-disabled + .MuiSwitch-track': {
          backgroundColor: colors.neutralVariant[200],
          borderColor: colors.neutralVariant[200],
          opacity: 1,
        },
      },

      /* ---------- THUMB (nativo) ---------- */
      '& .MuiSwitch-thumb': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },

      /* ---------- TRACK (píldora) ---------- */
      '& .MuiSwitch-track': {
        borderRadius: radius.full,
        opacity: 1,
        boxSizing: 'border-box',
        transition: theme.transitions.create(['background-color', 'border']),
      },
    };
  }
);

/* ---------- CÍRCULO INTERNO (handle real) ---------- */
const ThumbInner = styled('div', {
  shouldForwardProp: (prop) =>
    !['checked', 'small', 'disabled', 'paletteMain'].includes(prop as string),
})<{ checked: boolean; small: boolean; disabled?: boolean; paletteMain: string }>(
  ({ theme, checked, small, disabled, paletteMain }) => {
    const getThumbSize = () => {
      if (small) {
        return checked ? 18 : 14;
      }
      return checked ? 24 : 18;
    };
    const thumbSize = getThumbSize();

    return {
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: theme.transitions.create(['background-color', 'filter', 'box-shadow']),

      width: thumbSize,
      height: thumbSize,

      // estado visual ON/OFF
      ...(checked
        ? { backgroundColor: colors.specialColors.white, color: paletteMain, boxShadow: 'none' }
        : { backgroundColor: '#535354', color: '#FFFFFF', boxShadow: 'none' }),

      // disabled
      ...(disabled && {
        backgroundColor: colors.neutralVariant[300],
        color: '#FFFFFF',
        filter: 'none',
        boxShadow: 'none',
      }),
    };
  }
);

interface ThumbProps {
  checked: boolean;
  small: boolean;
  hasIcon: boolean;
  disabled?: boolean;
  paletteMain: string;
}

const ThumbContent: React.FC<ThumbProps> = ({ checked, small, hasIcon, disabled, paletteMain }) => {
  const iconName = checked ? 'check' : 'remove';

  return (
    <ThumbInner
      checked={checked}
      small={small}
      disabled={disabled}
      paletteMain={paletteMain}
      className={clsx(checked ? THUMB_CHECKED_CLASS : THUMB_UNCHECKED_CLASS)}
    >
      {hasIcon && <UIKitIcon name={iconName} size={small ? 's' : 'm'} />}
    </ThumbInner>
  );
};

const UIKitToggle: React.FC<UIKitToggleProps> = ({
  size = 'medium',
  hasIcon = false,
  disabled,
  checked,
  color = 'primary',
  ...rest
}) => {
  const palette = uikitPalette[color];
  const small = size === 'small';

  return (
    <StyledSwitch
      disableRipple
      checked={checked}
      disabled={disabled}
      color="default"
      uiSize={size}
      paletteMain={palette.main}
      paletteLight={palette.light}
      icon={
        <ThumbContent
          checked={false}
          small={small}
          hasIcon={hasIcon}
          disabled={disabled}
          paletteMain={palette.main}
        />
      }
      checkedIcon={
        <ThumbContent
          checked
          small={small}
          hasIcon={hasIcon}
          disabled={disabled}
          paletteMain={palette.main}
        />
      }
      {...rest}
    />
  );
};

export default UIKitToggle;
